import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, Search, ChevronDown, ExternalLink, Loader2, BedDouble, Shield, Clock, CreditCard, ArrowRight, Car, Plane as PlaneIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import api from '../services/api';

const AFFILIATE_LINKS = {
  welcomePickups: 'https://tpm.lv/sD3CoTaI',
  getTransfer: 'https://gettransfer.tpm.lv/CDo528tq',
};

export default function HotelSearchPage() {
  const navigate = useNavigate();
  const [destination, setDestination] = useState('');
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [loading, setLoading] = useState(false);
  const [popularDests, setPopularDests] = useState([]);

  useEffect(() => {
    api.get('/hotels/popular-destinations').then(r => setPopularDests(r.data.destinations || [])).catch(() => {});
  }, []);

  const handleSearch = async (e, provider) => {
    if (e) e.preventDefault();
    if (!destination || !checkin || !checkout) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ destination, checkin, checkout, guests: String(guests), rooms: String(rooms) });
      const res = await api.get(`/hotels/booking-link?${params}`);
      if (res.data.success) {
        const url = provider === 'expedia' ? res.data.expedia_url : res.data.booking_url;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  const handlePopularDest = (dest) => {
    setDestination(dest.name);
    const ci = new Date(); ci.setDate(ci.getDate() + 14);
    const co = new Date(); co.setDate(co.getDate() + 18);
    setCheckin(ci.toISOString().split('T')[0]);
    setCheckout(co.toISOString().split('T')[0]);
  };

  const handleQuickSearch = (dest) => {
    const ci = new Date(); ci.setDate(ci.getDate() + 14);
    const co = new Date(); co.setDate(co.getDate() + 18);
    setDestination(dest.name);
    setCheckin(ci.toISOString().split('T')[0]);
    setCheckout(co.toISOString().split('T')[0]);
    setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          destination: dest.name, checkin: ci.toISOString().split('T')[0],
          checkout: co.toISOString().split('T')[0], guests: String(guests), rooms: String(rooms),
        });
        const res = await api.get(`/hotels/booking-link?${params}`);
        if (res.data.success && res.data.expedia_url) {
          window.open(res.data.expedia_url, '_blank', 'noopener,noreferrer');
        }
      } catch { /* silent */ }
    }, 100);
  };

  const isFormReady = destination && checkin && checkout;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="hotel-search-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-20 left-10 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 right-20 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Dashboard
            </button>
          </div>
          <div className="text-center mb-8" data-testid="hotel-hero">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BedDouble className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Discover Your Ideal Stay</h1>
            <p className="text-white/80">Compare prices across Booking.com & Expedia — find the best deal</p>
          </div>

          {/* Search Form */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-6 md:p-8 mb-8" data-testid="hotel-search-form">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-2">
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Destination</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <MapPin className="w-4 h-4 text-ocean/40 mr-2" />
                    <input type="text" value={destination} onChange={e => setDestination(e.target.value)} placeholder="Where are you going?"
                      className="bg-transparent text-ocean placeholder:text-ocean/30 outline-none w-full text-sm"
                      data-testid="hotel-search-input-destination" />
                  </div>
                </div>
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Check-in</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Calendar className="w-4 h-4 text-ocean/40 mr-2" />
                    <input type="date" value={checkin} onChange={e => setCheckin(e.target.value)}
                      className="bg-transparent text-ocean outline-none w-full text-sm"
                      data-testid="hotel-search-input-checkin" />
                  </div>
                </div>
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Check-out</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Calendar className="w-4 h-4 text-ocean/40 mr-2" />
                    <input type="date" value={checkout} onChange={e => setCheckout(e.target.value)}
                      className="bg-transparent text-ocean outline-none w-full text-sm"
                      data-testid="hotel-search-input-checkout" />
                  </div>
                </div>
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Guests</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Users className="w-4 h-4 text-ocean/40 mr-2" />
                    <select value={guests} onChange={e => setGuests(Number(e.target.value))}
                      className="bg-transparent text-ocean outline-none w-full text-sm appearance-none cursor-pointer"
                      data-testid="hotel-search-input-guests">
                      {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-ocean/40" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Rooms</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <BedDouble className="w-4 h-4 text-ocean/40 mr-2" />
                    <select value={rooms} onChange={e => setRooms(Number(e.target.value))}
                      className="bg-transparent text-ocean outline-none w-full text-sm appearance-none cursor-pointer"
                      data-testid="hotel-search-input-rooms">
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Room' : 'Rooms'}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-ocean/40" />
                  </div>
                </div>
              </div>

              {/* Dual Search Buttons */}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={(e) => handleSearch(e, 'expedia')} disabled={loading || !isFormReady}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-full px-8 py-3 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 text-sm w-full sm:w-auto"
                  data-testid="hotel-search-expedia">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search on Expedia <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
                <Button onClick={(e) => handleSearch(e, 'booking')} disabled={loading || !isFormReady}
                  variant="outline"
                  className="border-2 border-ocean/40 text-ocean hover:bg-ocean/10 font-semibold rounded-full px-8 py-3 shadow-md hover:shadow-lg transition-all disabled:opacity-50 text-sm w-full sm:w-auto"
                  data-testid="hotel-search-booking">
                  <Search className="w-4 h-4 mr-2" />
                  Search on Booking.com <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
              </div>

              <p className="text-center text-ocean/50 text-xs mt-3">
                Compare prices across both platforms — your search details are passed automatically
              </p>
            </form>
          </div>

          {/* Trust Signals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              { icon: Shield, title: 'Secure Booking', desc: 'Book directly on trusted platforms with full buyer protection' },
              { icon: CreditCard, title: 'Best Price Guarantee', desc: 'Compare Booking.com & Expedia to find the lowest rates' },
              { icon: Clock, title: 'Free Cancellation', desc: 'Most properties offer free cancellation — book risk-free' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-gold/15 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-ocean text-sm font-semibold mb-1">{title}</h3>
                <p className="text-ocean/50 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Travel Services */}
          <div className="mb-10">
            <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-6">Complete Your Travel</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <a href={AFFILIATE_LINKS.welcomePickups} target="_blank" rel="noopener noreferrer"
                className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg p-6 hover:shadow-xl transition-all hover:border-gold/40"
                data-testid="welcome-pickups-card">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-ocean font-semibold mb-1">Airport Pickup Service</h3>
                    <p className="text-ocean/60 text-xs mb-3">Professional drivers waiting for you at the airport. Pre-book your pickup for a stress-free arrival.</p>
                    <span className="inline-flex items-center gap-1.5 text-green-600 font-semibold text-sm group-hover:text-green-700 transition-colors">
                      Book on Welcome Pickups <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </a>

              <a href={AFFILIATE_LINKS.getTransfer} target="_blank" rel="noopener noreferrer"
                className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg p-6 hover:shadow-xl transition-all hover:border-gold/40"
                data-testid="get-transfer-card">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ocean to-sky flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-ocean font-semibold mb-1">Private Transfers</h3>
                    <p className="text-ocean/60 text-xs mb-3">Door-to-door private transfers between cities, airports, and hotels. Compare driver offers instantly.</p>
                    <span className="inline-flex items-center gap-1.5 text-ocean font-semibold text-sm group-hover:text-sky transition-colors">
                      Book on GetTransfer <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Popular Destinations */}
          <div data-testid="popular-destinations">
            <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-6">Popular Destinations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popularDests.map((dest, i) => (
                <div key={dest.name} className="group relative overflow-hidden rounded-2xl border border-white/20 hover:border-gold/50 transition-all shadow-lg">
                  <img src={dest.image} alt={dest.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-white text-lg font-semibold">{dest.name}</p>
                    <p className="text-white/60 text-sm">{dest.country}</p>
                    <p className="text-white/40 text-xs mt-1">From ${dest.price_from}/night</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => handlePopularDest(dest)}
                        className="bg-white/15 backdrop-blur-md text-white text-xs rounded-full px-3 py-1.5 hover:bg-white/25 transition-colors border border-white/20"
                        data-testid={`popular-dest-fill-${i}`}>
                        Fill Search
                      </button>
                      <button onClick={() => handleQuickSearch(dest)}
                        className="bg-gradient-to-r from-ocean to-sky text-white text-xs font-semibold rounded-full px-3 py-1.5 hover:shadow-lg transition-all flex items-center gap-1"
                        data-testid={`popular-dest-search-${i}`}>
                        Search Now <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-12 mb-4">
            <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-8 text-center">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', title: 'Enter Your Details', desc: 'Fill in your destination, dates, and number of guests above' },
                { step: '02', title: 'Choose Your Platform', desc: 'Search on Booking.com or Expedia — compare prices on both' },
                { step: '03', title: 'Book with Confidence', desc: 'Complete your reservation directly on the platform you choose' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="text-center bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/15">
                  <div className="text-gold text-4xl font-bold font-serif mb-3">{step}</div>
                  <h3 className="text-white text-sm font-semibold mb-2">{title}</h3>
                  <p className="text-white/60 text-xs leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <div className="flex justify-center pb-8 relative z-10">
        <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
          <ArrowRight className="w-4 h-4 rotate-180" /> Back to Dashboard
        </button>
      </div>

      <footer className="border-t border-white/10 py-8 px-4 text-center relative z-10">
        <p className="text-white/40 text-xs">Powered by Booking.com & Expedia &middot; Triple A Travel Solutions</p>
      </footer>
    </div>
  );
}
